import { createContext, useContext, useEffect, ReactNode } from 'react';
import { IntercomProvider as ReactUseIntercomProvider, useIntercom } from 'react-use-intercom';
import { useAuthStore } from '@/store/useAuthStore';
import { getToken } from '@/helper/auth';
import { INTERCOM_TOKEN_COOKIE_NAME } from '@/_lib/constants';
import { config } from '@/env';

interface IntercomContextType {
  boot: (data?: any) => void;
  shutdown: () => void;
  update: (data?: any) => void;
  hide: () => void;
  show: () => void;
  showMessages: () => void;
  showNewMessage: (message?: string) => void;
  trackEvent: (eventName: string, metadata?: any) => void;
  getVisitorId: () => string | undefined;
}

const IntercomContext = createContext<IntercomContextType | undefined>(undefined);

interface IntercomProviderProps {
  children: ReactNode;
}

export function IntercomProvider({ children }: IntercomProviderProps) {
  return (
    <ReactUseIntercomProvider appId={config.VITE_INTERCOM_APP_ID || ''}>
      <IntercomInitializer>{children}</IntercomInitializer>
    </ReactUseIntercomProvider>
  );
}

function IntercomInitializer({ children }: { children: ReactNode }) {
  const intercom = useIntercom();
  const user = useAuthStore(state => state.user);
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const token = getToken(INTERCOM_TOKEN_COOKIE_NAME);

  const { id, firstName, lastName, email } = user || {};

  useEffect(() => {
    if (!isAuthenticated || !id) return;
    intercom.boot();

    intercom.update({
      name: `${firstName} ${lastName}`.trim(),
      email,
      userId: id,
      phone: user.phone ?? '',
      createdAt: new Date(user.createdAt).toISOString(),
      customAttributes: {
        intercom_user_jwt: token,
        organizationName: user.organizationName,
        organizationId: user.organizationId,
        isVerified: user.isVerified,
        isOnboarded: user.isOnboarded,
        role: user.role,
        status: user.status
      }
    });
  }, [intercom, isAuthenticated, id, firstName, lastName, email, token, user]);

  return <IntercomContext.Provider value={intercom}>{children}</IntercomContext.Provider>;
}

export function useIntercomContext() {
  const context = useContext(IntercomContext);
  if (context === undefined) {
    throw new Error('useIntercomContext must be used within an IntercomProvider');
  }
  return context;
}
